import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import QuizTaker from '../../components/QuizTaker';
import { authHelpers } from '../../firebaseClient';
import { getCourse } from '../../utils/courseHelpers';
import { getUserEnrollment, isCapstoneUnlocked, areAllLessonsComplete, submitCapstone, markEnrollmentCertificateIssued } from '../../utils/enrollmentHelpers';
import { getOrganization } from '../../utils/orgHelpers';
import { issueCertificate } from '../../utils/certHelpers';

const CourseCapstone = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgId = searchParams?.get('orgId');
  const courseId = searchParams?.get('courseId');

  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [course, setCourse] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [issuingCert, setIssuingCert] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !courseId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const { session } = await authHelpers.getSession();
      if (!session) {
        navigate('/authentication-portal', { replace: true });
        return;
      }
      setCurrentUser(session.user);

      const { course: courseData } = await getCourse(orgId, courseId);
      if (!courseData?.capstone) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const { organization: orgData } = await getOrganization(orgId);

      const { enrollment: enrollmentData } = await getUserEnrollment(session.user.uid, courseId);
      if (!enrollmentData) {
        navigate(`/course-preview?orgId=${orgId}&courseId=${courseId}`, { replace: true });
        return;
      }

      setCourse(courseData);
      setOrganization(orgData);
      setEnrollment(enrollmentData);
      setLoading(false);
    };

    load();
  }, [orgId, courseId, navigate]);

  const maybeIssueCertificate = async (updatedEnrollment) => {
    if (!updatedEnrollment?.capstoneResult?.certifiedEligible) return updatedEnrollment;
    if (updatedEnrollment?.certificateId) return updatedEnrollment;

    setIssuingCert(true);
    const { certificate, error } = await issueCertificate({
      userId: currentUser?.uid,
      userName: currentUser?.displayName || currentUser?.email?.split('@')?.[0],
      courseId,
      courseTitle: course?.title,
      orgId,
      orgName: organization?.name,
      orgVerified: organization?.verified,
      score: updatedEnrollment?.capstoneResult?.bestScore,
    });
    setIssuingCert(false);

    if (error || !certificate) return updatedEnrollment;

    await markEnrollmentCertificateIssued(currentUser?.uid, courseId, certificate?.certId).catch(() => {});
    return { ...updatedEnrollment, certificateId: certificate?.certId };
  };

  const handleSubmit = async (answers) => {
    const { result, error } = await submitCapstone({
      userId: currentUser?.uid,
      courseId,
      capstone: course?.capstone,
      answers,
      certification: course?.certification,
      allowRetake: areAllLessonsComplete(course, enrollment),
    });

    if (result) {
      const { enrollment: refreshed } = await getUserEnrollment(currentUser?.uid, courseId);
      if (refreshed) {
        const withCert = await maybeIssueCertificate(refreshed);
        setEnrollment(withCert);
      }
    }
    return { result, error };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <Icon name="SearchX" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">No capstone exam here</h1>
          <p className="text-muted-foreground mb-6">This course doesn't have a capstone, or you're not enrolled in it.</p>
          <Link to="/course-catalog">
            <Button variant="default">Back to Catalog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const unlocked = isCapstoneUnlocked(course, enrollment);
  const modulesWithQuizzes = course?.modules?.filter((m) => !!m?.quiz) || [];
  const incompleteModules = modulesWithQuizzes?.filter((m) => !enrollment?.moduleQuizResults?.[m?.id]?.passed);
  const allLessonsDone = enrollment?.progressPercent >= 100 || (() => {
    const flat = [];
    course?.modules?.forEach((m) => m?.lessons?.forEach((l) => flat.push(l)));
    return flat?.every((l) => enrollment?.completedLessonIds?.includes(l?.id));
  })();

  return (
    <>
      <Helmet>
        <title>{course?.capstone?.title} - {course?.title} - Nesolearn</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavigationBar />

        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to={`/course-preview?orgId=${orgId}&courseId=${courseId}`}>
            <Button variant="ghost" iconName="ArrowLeft" iconPosition="left" className="mb-6">
              Back to Course
            </Button>
          </Link>

          {enrollment?.certificateId ? (
            <div className="bg-success/10 border border-success/20 rounded-xl p-6 mb-6 text-center">
              <Icon name="Award" size={32} className="text-success mx-auto mb-3" />
              <h2 className="text-lg font-bold text-foreground mb-1">Certificate Issued</h2>
              <p className="text-sm text-muted-foreground mb-4">
                You've earned a verifiable certificate for completing {course?.title}.
              </p>
              <Link to={`/verify/${enrollment?.certificateId}`}>
                <Button variant="default" iconName="ExternalLink" iconPosition="right">
                  View Certificate
                </Button>
              </Link>
            </div>
          ) : (
            enrollment?.capstoneResult?.certifiedEligible && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6 flex items-center gap-3">
                <Icon name="Award" size={22} className="text-success" />
                <p className="text-sm text-success font-medium">
                  {issuingCert ? 'Issuing your certificate…' : "You've met the certification requirements for this course."}
                </p>
              </div>
            )
          )}

          {!unlocked ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Lock" size={24} className="text-muted-foreground" />
              </div>
              <h1 className="text-xl font-bold text-foreground mb-2">Capstone Locked</h1>
              <p className="text-muted-foreground mb-6">Complete the following before you can take the capstone exam.</p>

              <div className="text-left max-w-sm mx-auto space-y-2">
                {!allLessonsDone && (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Icon name="Circle" size={14} className="text-muted-foreground" />
                    Finish all lessons in the course
                  </div>
                )}
                {incompleteModules?.map((m) => (
                  <div key={m?.id} className="flex items-center gap-2 text-sm text-foreground">
                    <Icon name="Circle" size={14} className="text-muted-foreground" />
                    Pass the quiz for "{m?.title}"
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <QuizTaker
              title={course?.capstone?.title}
              questions={course?.capstone?.questions}
              passingScore={course?.capstone?.passingScore}
              onSubmit={handleSubmit}
              lastResult={
                enrollment?.capstoneResult
                  ? {
                      score: enrollment?.capstoneResult?.bestScore,
                      passed: enrollment?.capstoneResult?.passed,
                      answers: enrollment?.capstoneResult?.answers,
                      pointsAwarded: 0,
                    }
                  : null
              }
              allowRetake={areAllLessonsComplete(course, enrollment)}
              submitLabel="Submit Capstone"
            />
          )}
        </main>
      </div>
    </>
  );
};

export default CourseCapstone;